/**
 * Browser-only webp decode for {@link createTileReliefSampler}'s class maps.
 * Kept out of `tileRelief.ts` so vitest never touches WebGL or OffscreenCanvas.
 */
import type { DecodedClassMap } from './tileRelief';

/** Fetches and decodes one equirectangular class-map webp, row 0 north. Canvas 2D `getImageData` is forbidden here: the plains weight
 *  lives in alpha and is 0 across the dune seas, so premultiplied alpha would
 *  zero it - the WebGL readback below is unpremultiplied end to end. */
export async function decodeClassMapWebp(url: string): Promise<DecodedClassMap> {
  const res = await fetch(url);
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob, {
    premultiplyAlpha: 'none',
    colorSpaceConversion: 'none',
  });
  const { width, height } = bitmap;

  const canvas = new OffscreenCanvas(width, height);
  const gl = canvas.getContext('webgl2');
  if (!gl) throw new Error('tileReliefDecode: WebGL2 unavailable');

  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

  const framebuffer = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

  // No flip either way: the upload puts the image's top row at texel row 0
  // and readPixels hands row 0 back first, so north is already row 0.
  const data = new Uint8Array(width * height * 4);
  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, data);
  gl.getExtension('WEBGL_lose_context')?.loseContext();
  bitmap.close();
  return { data, width, height };
}
